/** What the banner and the dialogs read: connectivity, the queue and the notices, live. */
import { liveQuery } from 'dexie';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useSession } from '../app/api.js';
import { connectivity, useConnectivity, type Connectivity } from './connectivity.js';
import { db, type QueuedCommand } from './db.js';
import { notices, syncStatus, type SyncNotice, type SyncState } from './sync.js';
import { isOfflineSession } from './session-core.js';

export interface OfflineStatus {
  connectivity: Connectivity;
  /** Saved changes waiting to be sent. */
  pending: number;
  /** Saved changes the server refused: a person must look. */
  problems: QueuedCommand[];
  notices: SyncNotice[];
  sync: SyncState;
  /** Signed in on this till without the cloud: the real sign-in happens when it is back. */
  localSession: boolean;
}

function useLive<T>(query: () => Promise<T>, initial: T): T {
  const [value, setValue] = useState<T>(initial);
  useEffect(() => {
    const sub = liveQuery(query).subscribe({
      next: setValue,
      error: () => undefined,
    });
    return () => sub.unsubscribe();
    // The query reads the current store each time it runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return value;
}

export function useOfflineStatus(outletId: string | null): OfflineStatus {
  const online = useConnectivity();
  const sync = useSyncExternalStore(syncStatus.subscribe, syncStatus.get, syncStatus.get);
  const session = useSession();
  const pending = useLive(() => db().queue.where('status').equals('queued').count(), 0);
  const problems = useLive(
    () => db().queue.where('status').anyOf('conflict', 'rejected').toArray(),
    [] as QueuedCommand[],
  );
  const held = useLive(async () => (outletId ? notices(outletId) : []), [] as SyncNotice[]);
  return {
    connectivity: online,
    pending,
    problems,
    notices: held,
    sync,
    localSession: isOfflineSession(session),
  };
}

export { connectivity };
